import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { JumptechDate } from '@jump-tech-frontend/domain';

@Injectable({
  providedIn: 'root'
})
export class DateQuestionService {
  private isoDateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})?$/;

  canParseDateInput(value: string): boolean {
    // the method is only referenced in the spec file
    // but maybe useful in future if we want allow other input formats besides 'YYYY-MM-DD'
    const jumptechDate = JumptechDate.from(value);
    return jumptechDate?.isValid() ?? false;
  }

  public valueIsDateIso(value: string | null | undefined): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }

    // First check we are a valid date
    const jumptechDate = JumptechDate.from(value);
    if (!jumptechDate?.isValid()) {
      return false;
    }

    // Check for full ISO string with time component
    return this.isoDateTimeRegex.test(value);
  }

  extractDateFromIso(isoValue: string): string {
    return isoValue ? isoValue.split('T')[0] : '';
  }

  updateDatePreservingTime(newDateValue: string, originalIsoValue: string): string {
    if (!originalIsoValue || !newDateValue) {
      return originalIsoValue;
    }

    // Validate the original ISO value
    const originalDate = JumptechDate.from(originalIsoValue);
    if (!originalDate?.isValid()) {
      return originalIsoValue;
    }

    // Validate the new date
    const newDateTest = JumptechDate.from(newDateValue);
    if (!newDateTest?.isValid()) {
      return originalIsoValue;
    }

    // Replace YYYY-MM-DD part while keeping everything after the 'T'
    const timePart = originalIsoValue.substring(originalIsoValue.indexOf('T'));
    return newDateValue + timePart;
  }

  /**
   * Creates a display form for ISO date inputs with change handler
   */
  public buildDateDisplayForm(questionKey: string, isoValue: string, mainForm: UntypedFormGroup): UntypedFormGroup {
    const displayForm = new UntypedFormGroup({});
    const dateOnlyValue = this.extractDateFromIso(isoValue);

    displayForm.addControl(questionKey + '__display', new UntypedFormControl(dateOnlyValue));

    displayForm.get(questionKey + '__display')?.valueChanges.subscribe(val => {
      if (val) {
        const updatedIsoValue = this.updateDatePreservingTime(val, isoValue);
        mainForm.get(questionKey)?.setValue(updatedIsoValue);
      }
    });

    return displayForm;
  }
}
